/**
 * Modal for adding a PA status row on the Letter Upload tab. Collects bucket,
 * include-drug-name, document type, and document name before appending to the table.
 */
import { useEffect, useMemo, useState } from "react";
import { closeModal, Modal, Select, TextInput } from "risa-oasis-ui_v2";
import CustomRadioButton from "../../../../../components/customRadioButton/customRadioButton";
import { buildAvailableBucketOptions } from "../shared/paStatusBucketOptions";

export const ADD_NEW_LETTER_UPLOAD_STATUS_MODAL_ID =
  "letter-upload-add-new-status-modal";

export interface NewLetterUploadStatusInput {
  bucket: string;
  includeDrugName: boolean;
  documentType: string;
  documentName: string;
}

interface AddNewStatusModalProps {
  usedBuckets: string[];
  onConfirm: (input: NewLetterUploadStatusInput) => void;
}

const DEFAULT_FORM: NewLetterUploadStatusInput = {
  bucket: "",
  includeDrugName: true,
  documentType: "",
  documentName: "",
};

/**
 * Form modal opened from the Letter Upload Eligible PA Statuses toolbar action.
 *
 * @param usedBuckets - Bucket ids already in the table
 * @param onConfirm - Called with validated form values when the user clicks Add
 * @returns Add New Status modal for Letter Upload
 */
const AddNewStatusModal = ({
  usedBuckets,
  onConfirm,
}: AddNewStatusModalProps) => {
  const [form, setForm] = useState<NewLetterUploadStatusInput>(DEFAULT_FORM);
  const [error, setError] = useState("");
  const [fieldResetKey, setFieldResetKey] = useState(0);

  const bucketOptions = useMemo(
    () => buildAvailableBucketOptions(usedBuckets),
    [usedBuckets],
  );

  useEffect(() => {
    setError("");
  }, [form.bucket]);

  const reset = () => {
    setForm(DEFAULT_FORM);
    setError("");
    setFieldResetKey((key) => key + 1);
  };

  const handleSave = () => {
    if (!form.bucket) {
      setError("Select a bucket");
      return;
    }
    if (usedBuckets.includes(form.bucket)) {
      setError("This bucket is already in the table");
      return;
    }
    onConfirm({
      ...form,
      documentType: form.documentType.trim(),
      documentName: form.documentName.trim(),
    });
    reset();
    closeModal(ADD_NEW_LETTER_UPLOAD_STATUS_MODAL_ID);
  };

  return (
    <Modal
      dialogId={ADD_NEW_LETTER_UPLOAD_STATUS_MODAL_ID}
      title="Add New Status"
      saveButtonText="Add"
      cancelText="Cancel"
      autoCloseOnSave={false}
      disableSave={!form.bucket || bucketOptions.length === 0}
      onSave={handleSave}
      onCancel={reset}
      onClose={reset}
    >
      <div className="flex flex-col gap-4 py-2">
        <Select
          id="letter-upload-add-status-bucket"
          label="Bucket"
          placeholder="Select"
          options={bucketOptions}
          value={form.bucket}
          defaultValue={form.bucket}
          onOptionChange={(option) =>
            setForm((current) => ({ ...current, bucket: option.value }))
          }
        />
        {error ? (
          <p className="text-tiny text-red-600" role="alert">
            {error}
          </p>
        ) : null}
        <div className="flex flex-col gap-2">
          <span className="text-small font-medium text-primaryGray-1">
            Include Drug Name in document name
          </span>
          <div className="flex flex-row gap-4">
            <CustomRadioButton
              name="letter-upload-include-drug-name"
              value="yes"
              label="Yes"
              checked={form.includeDrugName}
              onChange={() =>
                setForm((current) => ({ ...current, includeDrugName: true }))
              }
            />
            <CustomRadioButton
              name="letter-upload-include-drug-name"
              value="no"
              label="No"
              checked={!form.includeDrugName}
              onChange={() =>
                setForm((current) => ({ ...current, includeDrugName: false }))
              }
            />
          </div>
        </div>
        <TextInput
          key={`letter-upload-document-type-${fieldResetKey}`}
          id="letter-upload-document-type"
          label="Document Type"
          placeholder="Enter Document Type"
          defaultValue={form.documentType}
          onChange={(data) =>
            setForm((current) => ({ ...current, documentType: data.value }))
          }
          onBlur={(data) =>
            setForm((current) => ({ ...current, documentType: data.value }))
          }
        />
        <TextInput
          key={`letter-upload-document-name-${fieldResetKey}`}
          id="letter-upload-document-name"
          label="Document Name"
          placeholder="Enter Document Name"
          defaultValue={form.documentName}
          onChange={(data) =>
            setForm((current) => ({ ...current, documentName: data.value }))
          }
          onBlur={(data) =>
            setForm((current) => ({ ...current, documentName: data.value }))
          }
        />
      </div>
    </Modal>
  );
};

export default AddNewStatusModal;
